import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Progress,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
} from "@chakra-ui/react";
import moment from "moment";
import VendasBolePixLoteApi from "../../../../services/VendasBoletoloteApi";
import { dateForList } from "../../../../constants/dataFormat";

interface Props {
  idLote: string;
  // compact = só barra + contagens (sem a tabela por parcela).
  compact?: boolean;
  /**
   * Chamado UMA vez, quando a geração termina. Existe porque este painel tem
   * polling próprio e a tela que o contém não: sem o aviso, a barra chegava a
   * 4/4 e a tabela de parcelas logo abaixo continuava "Não emitido" até o F5.
   *
   * "Terminou" NÃO quer dizer "deu tudo certo" — um lote em que toda parcela
   * estourou o teto de tentativas também termina. Por isso o resumo vai junto:
   * quem liga o botão do carnê precisa de `emitidas === total`, não de
   * "acabou".
   */
  onConcluido?: (resumo: {
    total: number;
    emitidas: number;
    falhas: number;
  }) => void;
}

const badgeStatus = (status: string) => {
  if (status === "EMITIDO") return <Badge colorScheme="green">Emitido</Badge>;
  if (status === "FALHA") return <Badge colorScheme="red">Falha</Badge>;
  return <Badge colorScheme="yellow">Pendente</Badge>;
};

export default function LoteStatusProgresso({ idLote, compact, onConcluido }: Props) {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<any>(null);
  // A parada do polling NÃO pode depender de `timerRef.current` já ter sido
  // atribuído: a primeira chamada sai antes do setInterval, e um lote que já
  // chega concluído fazia o clear cair no vazio — a tela seguia consultando a
  // cada 4s para sempre. A flag é verificada no início de cada ciclo.
  const concluidoRef = useRef(false);
  // onConcluido em ref: se viesse na dependência do efeito, uma função recriada
  // a cada render do pai reiniciaria o polling.
  const onConcluidoRef = useRef(onConcluido);
  onConcluidoRef.current = onConcluido;

  useEffect(() => {
    if (!idLote) return;

    let ativo = true;
    concluidoRef.current = false;

    const pararPolling = () => {
      concluidoRef.current = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const carregar = async () => {
      if (concluidoRef.current) return;

      try {
        const data = await VendasBolePixLoteApi.getStatusLote(idLote);
        if (!ativo) return;
        setStatus(data);

        // Para de pollar quando não há mais parcelas pendentes.
        if (data?.concluido) {
          const jaAvisou = concluidoRef.current;
          pararPolling();
          if (!jaAvisou)
            onConcluidoRef.current?.({
              total: data.total ?? 0,
              emitidas: data.emitidas ?? 0,
              falhas: data.falhas ?? 0,
            });
        }
      } catch (e) {
        // silencioso — tenta de novo no próximo ciclo
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();
    timerRef.current = setInterval(carregar, 4000);
    return () => {
      ativo = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [idLote]);

  if (loading && !status) {
    return (
      <Flex align="center" gap={2} py={4}>
        <Spinner size="sm" />
        <Text>Carregando status da geração…</Text>
      </Flex>
    );
  }
  if (!status) return null;

  const total = status.total || 0;
  const emitidas = status.emitidas || 0;
  const pct = total > 0 ? Math.round((emitidas / total) * 100) : 0;
  // parcelas que ainda vão ser retentadas, mas já falharam ao menos uma vez
  const comFalha = status.comFalha || 0;
  const maxTentativas = status.maxTentativas || 5;

  return (
    <Box>
      <Flex justify="space-between" mb={1} fontSize="sm">
        <Text fontWeight="medium">
          {emitidas} de {total} emitidos{" "}
          {status.concluido ? "" : "— gerando as demais…"}
        </Text>
        <Text fontWeight="bold">{pct}%</Text>
      </Flex>
      <Progress
        value={pct}
        colorScheme={
          status.falhas > 0 ? "red" : comFalha > 0 ? "orange" : "green"
        }
        borderRadius="md"
        mb={3}
        hasStripe
        isAnimated={!status.concluido}
      />

      <Flex gap={2} mb={3} flexWrap="wrap">
        <Badge colorScheme="green">Emitidos: {emitidas}</Badge>
        <Badge colorScheme="yellow">Pendentes: {status.pendentes}</Badge>
        <Badge colorScheme="red">Falhas: {status.falhas}</Badge>
      </Flex>

      {/* Falha ainda em retentativa: sem este aviso a tela dizia apenas
          "gerando as demais…" enquanto a parcela ia acumulando erro, e só
          depois do teto de tentativas é que aparecia como falha. */}
      {comFalha > 0 && !status.concluido && (
        <Text fontSize="xs" color="orange.600" mb={3}>
          {comFalha === 1
            ? "1 parcela falhou e está sendo tentada de novo"
            : `${comFalha} parcelas falharam e estão sendo tentadas de novo`}{" "}
          (até {maxTentativas} tentativas).
        </Text>
      )}

      {status.concluido && status.falhas > 0 && (
        <Text fontSize="xs" color="red.600" mb={3}>
          {status.falhas === 1 ? "1 parcela não foi emitida" : `${status.falhas} parcelas não foram emitidas`}{" "}
          após {maxTentativas} tentativas. Use "Reemitir parcela" depois de
          resolver o motivo.
        </Text>
      )}

      {!compact && (
      <Box
        maxH="280px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th>Parcela</Th>
              <Th>Vencimento</Th>
              <Th isNumeric>Valor</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {status.parcelas?.map((p: any) => (
              <Tr key={p.id}>
                <Td>{p.parcela === 1 ? "1ª (entrada)" : `${p.parcela}ª`}</Td>
                <Td>{moment(p.dataVencimento).format(dateForList)}</Td>
                <Td isNumeric>
                  {Number(p.valor).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Td>
                <Td>
                  {badgeStatus(p.status)}
                  {/* o erro aparece também na PENDENTE que já falhou: é o que
                      permite agir antes de estourar o teto de tentativas */}
                  {p.ultimoErro && (p.status === "FALHA" || p.tentativas > 0) && (
                    <Text
                      fontSize="xs"
                      color={p.status === "FALHA" ? "red.500" : "orange.500"}
                    >
                      {p.ultimoErro}
                      {p.status !== "FALHA" && p.tentativas > 0
                        ? ` (tentativa ${p.tentativas} de ${maxTentativas})`
                        : ""}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      )}
    </Box>
  );
}
